import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { CheckIcon, UserGroupIcon, BuildingStorefrontIcon, ScissorsIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon, LockClosedIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Popover } from '@headlessui/react';
import { OWNER_PRICING_PLANS } from '../data/campClaimSeedData';
import SEO from '../components/SEO';
import confetti from 'canvas-confetti';
import { redirectToStripeCheckout, getPriceIdForPlan, isPlanConfigured } from '../lib/stripeCheckout';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import type { Camp } from '../types/database';
import { trackEvent } from '../lib/analytics';
import { track } from '../lib/track';
import { useUserTypes } from '../hooks/useUserTypes';
import { hasClaimedCamp } from '../lib/authHelpers';
import AlreadyClaimedModal from '../components/AlreadyClaimedModal';

type PricingAudience = 'owners' | 'parents';

function PlanInfoPopover({ plan, isLastCard }: { plan: typeof OWNER_PRICING_PLANS[0]; isLastCard?: boolean }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isOpen, setIsOpen] = useState(false);
  
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // For last card, align right edge; otherwise align left edge
      setPosition({
        top: rect.bottom + 8,
        left: isLastCard ? rect.right - 288 : rect.left // w-72 = 288px
      });
    }
  }, [isOpen, isLastCard]);
  
  return (
    <Popover className="relative inline-block ml-1.5">
      {({ open }) => {
        if (open !== isOpen) setIsOpen(open);
        return (
          <>
            <Popover.Button
              ref={buttonRef}
              className="inline-flex items-center justify-center rounded-full text-primary hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
              aria-label={`More information about ${plan.name}`}
            >
              <InformationCircleIcon className="h-5 w-5" />
            </Popover.Button>
            {open && createPortal(
              <Popover.Panel 
                static
                className="fixed z-[99999] w-72 px-4 sm:px-0"
                style={{ top: `${position.top}px`, left: `${position.left}px` }}
              >
                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="relative bg-white p-4">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {plan.description}
                    </p>
                  </div>
                </div>
              </Popover.Panel>,
              document.body
            )}
          </>
        );
      }}
    </Popover>
  );
}

export default function PricingOwnersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const { isCampOwner } = useUserTypes();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const discountCardRef = useRef<HTMLDivElement>(null);
  const cancelledToastShownRef = useRef(false);

  // Camp selection modal state
  const [showCampSelectionModal, setShowCampSelectionModal] = useState(false);
  const [selectedCampSlug, setSelectedCampSlug] = useState<string>('');
  const [selectedCampId, setSelectedCampId] = useState<string>('');
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null);
  const [pendingPlanId, setPendingPlanId] = useState<string>('');
  const [campSearchTerm, setCampSearchTerm] = useState('');
  const [campSearchResults, setCampSearchResults] = useState<Camp[]>([]);
  const [showCampSearchResults, setShowCampSearchResults] = useState(false);
  const [showAlreadyClaimedModal, setShowAlreadyClaimedModal] = useState(false);
  
  // Determine audience from URL
  const audience: PricingAudience = location.pathname.includes('/parents') ? 'parents' : 'owners';

  // Load camp from session storage on mount and claim it if not already claimed
  useEffect(() => {
    const loadCampFromSession = async () => {
      const campSlug = sessionStorage.getItem('pending_claim_camp_slug');
      const campId = sessionStorage.getItem('pending_claim_camp_id');
      
      if (campSlug && campId && session?.user?.id) {
        // Fetch camp details including claimed status
        try {
          const { data, error } = await supabase
            .from('camps')
            .select('id, name, slug, city, state, claimed, owner_id')
            .eq('id', campId)
            .single();
          
          if (error) throw error;
          if (data) {
            // If camp is not claimed, claim it now (free claim)
            if (!data.claimed) {
              const { error: claimError } = await supabase
                .from('camps')
                .update({
                  claimed: true,
                  owner_id: session.user.id,
                  claimed_at: new Date().toISOString()
                  // Note: plan_id is set later when they pay
                })
                .eq('id', campId);
              
              if (claimError) {
                console.error('Error claiming camp:', claimError);
                toast.error('Failed to claim camp. Please try again.', { duration: 5000 });
                return;
              }
              
              console.log('Camp claimed successfully (free claim)');
              // Refresh camp data
              const { data: updatedCamp } = await supabase
                .from('camps')
                .select('id, name, slug, city, state, claimed, owner_id')
                .eq('id', campId)
                .single();
              
              if (updatedCamp) {
                setSelectedCamp(updatedCamp);
                setSelectedCampSlug(updatedCamp.slug);
                setSelectedCampId(updatedCamp.id);
              }
            } else {
              // Camp already claimed, just set the state
              setSelectedCamp(data);
              setSelectedCampSlug(data.slug);
              setSelectedCampId(data.id);
            }
          }
        } catch (error) {
          console.error('Error fetching camp from session:', error);
        }
      } else if (campSlug && campId) {
        // Camp in session but no user logged in yet - just load the data
        try {
          const { data, error } = await supabase
            .from('camps')
            .select('id, name, slug, city, state, claimed, owner_id')
            .eq('id', campId)
            .single();
          
          if (error) throw error;
          if (data) {
            setSelectedCamp(data);
            setSelectedCampSlug(data.slug);
            setSelectedCampId(data.id);
          }
        } catch (error) {
          console.error('Error fetching camp from session:', error);
        }
      }
    };

    loadCampFromSession();
  }, [session]);

  // Detect payment cancellation from URL parameters or returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cancelled = params.get('cancelled');

    if (cancelled === 'true') {
      if (!cancelledToastShownRef.current) {
        cancelledToastShownRef.current = true;
        toast.error('Payment cancelled. You can try again when ready.', {
          duration: 5000,
          icon: '❌',
        });
        track('payment_cancelled', {
          persona: 'camp_owner',
          page: 'pricing_owners',
        });
      }
      navigate('/pricing/owners', { replace: true });
      return;
    }

    cancelledToastShownRef.current = false;

    // Detect if user just came back from Stripe without completing
    // (Stripe checkout was in session storage but no success page was reached)
    const wasInCheckout = sessionStorage.getItem('stripe_checkout_initiated');
    if (wasInCheckout) {
      // Clear the flag
      sessionStorage.removeItem('stripe_checkout_initiated');
      track('payment_cancelled', {
        persona: 'camp_owner',
        page: 'pricing_owners',
        reason: 'return_without_complete',
      });
      // Show cancellation message
      toast('Payment not completed. You can try again when ready.', {
        duration: 5000,
        icon: 'ℹ️',
      });
    }
  }, [location.search, navigate]);

  // Confetti celebration inside discount card
  useEffect(() => {
    if (audience !== 'owners' || !discountCardRef.current) return;
    
    const card = discountCardRef.current;
    
    // Create a canvas inside the card
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '10';
    canvas.width = card.offsetWidth;
    canvas.height = card.offsetHeight;
    
    card.appendChild(canvas);
    
    const myConfetti = confetti.create(canvas, {
      resize: true,
      useWorker: true
    });
    
    // Small delay for better effect
    const timer = setTimeout(() => {
      myConfetti({
        particleCount: 50,
        spread: 60,
        origin: { x: 0.5, y: 0.5 },
        colors: ['#5200C4', '#E68C35', '#64AAAD', '#FFD700', '#FF6B6B'],
        startVelocity: 20,
        gravity: 0.8,
        scalar: 0.8
      });
    }, 300);

    return () => {
      clearTimeout(timer);
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    };
  }, [audience]); // Run when audience changes

  const handleAudienceChange = (newAudience: PricingAudience) => {
    navigate(`/pricing/${newAudience}`);
  };

  // Camp search functionality
  const searchCamps = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setCampSearchResults([]);
      setShowCampSearchResults(false);
      return;
    }

    const { data, error } = await supabase
      .from('camps')
      .select('id, name, slug, city, state')
      .ilike('name', `%${searchTerm}%`)
      .limit(10);

    if (error) {
      console.error('Error searching camps:', error);
      setCampSearchResults([]);
      return;
    }

    setCampSearchResults(data || []);
    setShowCampSearchResults(true);
  };

  const handleCampSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCampSearchTerm(value);
    
    // Debounce search
    setTimeout(() => {
      searchCamps(value);
    }, 300);
  };

  const handleSelectCamp = async (camp: Camp) => {
    setSelectedCampSlug(camp.slug);
    setSelectedCampId(camp.id);
    setCampSearchTerm(camp.name);
    setShowCampSearchResults(false);
    
    // Claim the camp immediately when selected (free claim)
    if (session?.user?.id && !camp.claimed) {
      try {
        const { error: claimError } = await supabase
          .from('camps')
          .update({
            claimed: true,
            owner_id: session.user.id,
            claimed_at: new Date().toISOString()
            // Note: plan_id is set later when they pay
          })
          .eq('id', camp.id);
        
        if (claimError) {
          console.error('Error claiming camp:', claimError);
          toast.error('Failed to claim camp. Please try again.', { duration: 5000 });
          return;
        }
        
        console.log('Camp claimed successfully (free claim)');
        // Refresh camp data
        const { data: updatedCamp } = await supabase
          .from('camps')
          .select('id, name, slug, city, state, claimed, owner_id')
          .eq('id', camp.id)
          .single();
        
        if (updatedCamp) {
          setSelectedCamp(updatedCamp);
        }
      } catch (error) {
        console.error('Error claiming camp:', error);
        toast.error('Failed to claim camp. Please try again.', { duration: 5000 });
      }
    } else if (camp.claimed) {
      // Camp already claimed, just set the state
      setSelectedCamp(camp);
    }
  };

  const handleSelectPlan = async (planId: string) => {
    console.log('PricingOwnersPage: handleSelectPlan called with planId:', planId);
    
    // Track plan selection (dataLayer + PostHog for GTM/Meta)
    // Same event names as parents page; use persona + page to tell apart in GTM
    trackEvent('pricing_plan_selected', {
      plan_id: planId,
      plan_name: planId.charAt(0).toUpperCase() + planId.slice(1),
      page: 'pricing_owners',
      is_authenticated: !!session
    });
    track('pricing_upgrade_clicked', {
      persona: 'camp_owner',
      page: 'pricing_owners',
      plan_id: planId,
      billing_cycle: billingCycle,
      is_authenticated: !!session,
    });
    track('pricing_plan_selected', {
      persona: 'camp_owner',
      page: 'pricing_owners',
      plan_id: planId,
      billing_cycle: billingCycle,
      is_authenticated: !!session,
    });

    // Check if user is logged in first
    if (!session) {
      console.log('PricingOwnersPage: User not logged in, redirecting to login');
      trackEvent('pricing_checkout_blocked_no_auth', {
        plan_id: planId,
        redirect_to: 'login'
      });
      track('pricing_checkout_blocked_no_auth', {
        persona: 'camp_owner',
        page: 'pricing_owners',
        plan_id: planId,
        redirect_to: 'login',
      });
      navigate('/login?type=business&redirect=/pricing/owners');
      return;
    }
    
    console.log('PricingOwnersPage: Current sessionStorage state:', {
      pending_claim_camp_slug: sessionStorage.getItem('pending_claim_camp_slug'),
      pending_claim_camp_id: sessionStorage.getItem('pending_claim_camp_id'),
      pending_camp_slug: sessionStorage.getItem('pending_camp_slug'),
      pending_plan_name: sessionStorage.getItem('pending_plan_name')
    });
    
    // Handle Trailhead and Campfire plans
    if (planId === 'trailhead' || planId === 'campfire') {
      const priceId = getPriceIdForPlan(planId, billingCycle);
      if (priceId) {
        // Try to get camp slug and ID from URL state or sessionStorage
        const state = location.state as { campSlug?: string, campId?: string } | null;
        const campSlug = state?.campSlug || sessionStorage.getItem('pending_claim_camp_slug');
        const campId = state?.campId || sessionStorage.getItem('pending_claim_camp_id');
        
        console.log('PricingOwnersPage: Camp context check:', { 
          state, 
          campSlug, 
          campId, 
          fromState: state?.campSlug, 
          fromStorage: sessionStorage.getItem('pending_claim_camp_slug'),
          fromStorageId: sessionStorage.getItem('pending_claim_camp_id')
        });
        
        // If no camp context, show camp selection modal
        if (!campSlug || !campId) {
          console.log('PricingOwnersPage: No camp context found, showing camp selection modal');
          trackEvent('pricing_camp_selection_modal_shown', {
            plan_id: planId,
            reason: 'no_camp_context'
          });
          track('pricing_camp_selection_modal_shown', {
            persona: 'camp_owner',
            page: 'pricing_owners',
            plan_id: planId,
            reason: 'no_camp_context',
          });
          setPendingPlanId(planId);
          setShowCampSelectionModal(true);
          return;
        }
        
        // Check if user already has a claimed camp (and it's a different camp)
        if (session?.user?.id && campId) {
          const { data: ownedCamps } = await supabase
            .from('camps')
            .select('id')
            .eq('owner_id', session.user.id)
            .eq('claimed', true)
            .limit(1);
          
          if (ownedCamps && ownedCamps.length > 0) {
            // Check if the camp they're checking out for is the same one they own
            const ownsThisCamp = ownedCamps.some(camp => camp.id === campId);
            
            if (!ownsThisCamp) {
              // User owns a different camp - show modal
              setShowAlreadyClaimedModal(true);
              return;
            }
            // If they own this camp, allow checkout to proceed (they're upgrading)
          }
        }
        
        console.log('PricingOwnersPage: Camp context found, proceeding to checkout:', campSlug, campId);
        
        trackEvent('pricing_checkout_initiated', {
          plan_id: planId,
          camp_id: campId,
          camp_slug: campSlug,
          has_camp_context: true
        });
        track('pricing_checkout_initiated', {
          persona: 'camp_owner',
          page: 'pricing_owners',
          plan_id: planId,
          billing_cycle: billingCycle,
          camp_id: campId,
          has_camp_context: true,
        });
        try {
          await redirectToStripeCheckout({
            priceId,
            campId,
            campSlug: campSlug || undefined,
            planName: planId,
            userEmail: session?.user?.email,
          });
        } catch (error) {
          console.error('Error redirecting to checkout:', error);
          trackEvent('pricing_checkout_error', {
            plan_id: planId,
            error_message: error instanceof Error ? error.message : 'Unknown error',
            camp_id: campId
          });
          track('pricing_checkout_error', {
            persona: 'camp_owner',
            page: 'pricing_owners',
            plan_id: planId,
            error_message: error instanceof Error ? error.message : 'Unknown error',
          });
          toast.error(
            error instanceof Error ? error.message : 'Failed to start checkout. Please try again.',
            { duration: 5000 }
          );
        }
      } else {
        console.error(`${planId} Price ID not configured`);
        toast.error('Payment system not configured. Please contact support.', {
          duration: 5000,
        });
      }
    } else {
      console.log('Selected plan:', planId, '- Coming soon!');
      trackEvent('pricing_coming_soon_clicked', {
        plan_id: planId,
        plan_name: planId.charAt(0).toUpperCase() + planId.slice(1)
      });
      track('pricing_coming_soon_clicked', {
        persona: 'camp_owner',
        page: 'pricing_owners',
        plan_id: planId,
      });
      toast('This plan is coming soon! Stay tuned.', {
        icon: '🚀',
        duration: 3000,
      });
    }
  };

  const handleConfirmCampSelection = async () => {
    if (!selectedCampSlug || !selectedCampId) {
      toast.error('Please select a camp first', { duration: 3000 });
      return;
    }

    // Check if user already has a claimed camp (and it's a different camp)
    if (session?.user?.id) {
      const { data: ownedCamps } = await supabase
        .from('camps')
        .select('id')
        .eq('owner_id', session.user.id)
        .eq('claimed', true)
        .limit(1);
      
      if (ownedCamps && ownedCamps.length > 0) {
        // Check if the camp they selected is the same one they own
        const ownsThisCamp = ownedCamps.some(camp => camp.id === selectedCampId);
        
        if (!ownsThisCamp) {
          // User owns a different camp - show modal
          setShowCampSelectionModal(false);
          setSelectedCampSlug('');
          setSelectedCampId('');
          setCampSearchTerm('');
          setCampSearchResults([]);
          setShowAlreadyClaimedModal(true);
          return;
        }
        // If they own this camp, allow selection to proceed (they're upgrading)
      }
    }

    trackEvent('pricing_camp_selected_from_modal', {
      plan_id: pendingPlanId,
      camp_id: selectedCampId,
      camp_slug: selectedCampSlug
    });
    track('pricing_camp_selected_from_modal', {
      persona: 'camp_owner',
      page: 'pricing_owners',
      plan_id: pendingPlanId,
      camp_id: selectedCampId,
    });

    const pendingBilling = sessionStorage.getItem('pending_billing') as 'monthly' | 'annual' | null;
    const priceId = getPriceIdForPlan(pendingPlanId, pendingBilling || 'annual');
    if (priceId) {
      trackEvent('pricing_checkout_initiated', {
        plan_id: pendingPlanId,
        camp_id: selectedCampId,
        camp_slug: selectedCampSlug,
        has_camp_context: true,
        source: 'modal_selection'
      });
      const pendingBillingCycle = pendingBilling || 'annual';
      track('pricing_checkout_initiated', {
        persona: 'camp_owner',
        page: 'pricing_owners',
        plan_id: pendingPlanId,
        billing_cycle: pendingBillingCycle,
        camp_id: selectedCampId,
        source: 'modal_selection',
      });
      await redirectToStripeCheckout({
        priceId,
        campId: selectedCampId,
        campSlug: selectedCampSlug,
        planName: pendingPlanId,
        userEmail: session?.user?.email,
      });
    }

    setShowCampSelectionModal(false);
    setSelectedCampSlug('');
    setSelectedCampId('');
    setCampSearchTerm('');
    setCampSearchResults([]);
  };

  // Clear selected camp and session storage, and unclaim the camp
  const clearCampContext = async () => {
    // If camp is claimed by current user, unclaim it
    if (selectedCamp && selectedCamp.claimed && selectedCamp.owner_id === session?.user?.id) {
      try {
        console.log('Unclaiming camp:', {
          campId: selectedCamp.id,
          currentOwnerId: selectedCamp.owner_id,
          userId: session?.user?.id
        });
        
        const { data, error: unclaimError } = await supabase
          .from('camps')
          .update({
            claimed: false,
            owner_id: null,
            plan_id: null,
            claimed_at: null
          })
          .eq('id', selectedCamp.id)
          .select();
        
        if (unclaimError) {
          console.error('Error unclaiming camp:', {
            error: unclaimError,
            code: unclaimError.code,
            message: unclaimError.message,
            details: unclaimError.details,
            hint: unclaimError.hint
          });
          toast.error(`Failed to remove camp: ${unclaimError.message || 'Please try again.'}`, { duration: 5000 });
          return;
        }
        
        console.log('Camp unclaimed successfully:', data);
        toast.success('Camp removed from your account', { duration: 3000 });
      } catch (error: any) {
        console.error('Error unclaiming camp:', error);
        toast.error(`Failed to remove camp: ${error?.message || 'Please try again.'}`, { duration: 5000 });
        return;
      }
    }
    
    sessionStorage.removeItem('pending_claim_camp_slug');
    sessionStorage.removeItem('pending_claim_camp_id');
    setSelectedCamp(null);
    setSelectedCampSlug('');
    setSelectedCampId('');
    console.log('Camp context cleared from sessionStorage');
  };

  return (
    <div className="page-container">
      <SEO
        title="Camp Owner Pricing Plans | CampWhiz"
        description="Choose the perfect plan for your camp. Get discovered by local families, manage your listing, and grow your summer camp business."
        keywords="camp owner pricing, summer camp listing, camp management, camp advertising"
        url="/pricing/owners"
      />
      
      <div className="page-content">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
            {audience === 'owners' ? 'Plans for Camp Owners' : 'Plans for Parents'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {audience === 'owners' 
              ? 'Get found by local families and grow your camp'
              : 'Manage your summer camp planning with ease'}
          </p>
        </div>

        {/* Audience Selector - Hide if logged in as camp owner */}
        {!session || !isCampOwner ? (
          <div className="flex justify-center mb-12">
            <div className="inline-flex rounded-xl border-2 border-gray-200 bg-white p-1">
              <button
                onClick={() => handleAudienceChange('owners')}
                className={`
                  flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
                  ${audience === 'owners'
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                  }
                `}
              >
                <BuildingStorefrontIcon className="h-5 w-5" aria-hidden="true" />
                Camp Owners
              </button>
              <button
                onClick={() => handleAudienceChange('parents')}
                className={`
                  flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
                  ${audience === 'parents'
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                  }
                `}
              >
                <UserGroupIcon className="h-5 w-5" aria-hidden="true" />
                Parents
              </button>
            </div>
          </div>
        ) : null}

        {audience === 'owners' ? (
          <>
            {/* Selected Camp Display */}
            {selectedCamp && (
              <div className="max-w-md mx-auto mb-8">
                <div className="bg-white rounded-lg border-2 border-primary/20 p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">
                        {selectedCamp.claimed && selectedCamp.owner_id === session?.user?.id
                          ? 'Claimed Camp:'
                          : 'Claiming camp:'}
                      </p>
                      <h3 className="font-semibold">
                        <Link 
                          to={`/camps/${selectedCamp.slug}`}
                          className="text-primary hover:text-primary/80 transition-colors"
                        >
                          {selectedCamp.name}
                        </Link>
                      </h3>
                      <p className="text-sm text-gray-600">{selectedCamp.city}, {selectedCamp.state}</p>
                      {selectedCamp.claimed && selectedCamp.owner_id === session?.user?.id && (
                        <p className="text-xs text-green-600 mt-1">✓ This camp is already claimed by you</p>
                      )}
                    </div>
                    <button
                      onClick={clearCampContext}
                      className="ml-3 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                      title="Clear selection and search for another camp"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Early Bird Special - Coupon Style */}
            <div className="mb-10 flex justify-center">
              <div className="relative inline-block">
                {/* Single scissors icon along the top border */}
                <ScissorsIcon 
                  className="absolute -top-3 left-2 h-6 w-6 text-orange-500 z-10" 
                  aria-hidden="true" 
                />
                
                <div
                  ref={discountCardRef}
                  className="relative bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-dashed border-orange-300 rounded-lg px-6 py-6 overflow-hidden"
                >
                  <div className="flex items-center justify-center gap-6">
                    {/* 50% OFF Badge */}
                    <div className="bg-orange-500 text-white rounded-lg px-6 py-6 shadow-md text-center flex-shrink-0">
                      <p className="text-3xl font-bold leading-none">50%</p>
                      <p className="text-lg font-semibold tracking-wide -mt-1">
                        OFF <span className="text-2xl">🎉</span>
                      </p>
                    </div>
                    
                    {/* Text Content */}
                    <div className="text-left">
                      <div className="inline-block bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-2">
                        LIMITED TIME OFFER
                      </div>
                      <p className="text-lg font-semibold text-gray-900">
                        Founding Member Pricing
                      </p>
                      <p className="text-sm text-gray-700">
                        Join as an early partner and save on all plans
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Cycle Toggle */}
            <div className="flex flex-col items-center mb-8">
              <div className="inline-flex rounded-lg border-2 border-gray-200 bg-white p-1 mb-2">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`
                    px-6 py-2 rounded-md font-medium transition-all
                    ${billingCycle === 'monthly'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                    }
                  `}
                >
                  1 month
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`
                    px-6 py-2 rounded-md font-medium transition-all
                    ${billingCycle === 'annual'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                    }
                  `}
                >
                  12 months
                </button>
              </div>
              <p className="text-xs text-green-600 font-medium">
                2 months FREE with annual billing
              </p>
            </div>

            {/* Pricing Cards */}
            <div className="flex flex-wrap justify-center gap-6 max-w-7xl mx-auto">
          {(() => {
            const filteredPlans = OWNER_PRICING_PLANS.filter(plan => {
              // Hide FREE plan if user is logged in as camp owner
              if (session && isCampOwner && plan.isFree) {
                return false;
              }
              return true;
            });
            
            return filteredPlans.map((plan, index) => {
            const isPopular = plan.isPopular;
            const isLastCard = index === filteredPlans.length - 1;
            
            return (
              <div
                key={plan.id}
                className={`
                  relative rounded-2xl shadow-lg overflow-hidden
                  w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)] xl:w-[calc(25%-1.125rem)]
                  ${isPopular 
                    ? 'border-2 border-orange-400 transform lg:scale-105 bg-gradient-to-b from-orange-50 to-white' 
                    : 'border border-gray-200 bg-white'
                  }
                `}
              >
                {/* Most Popular Badge */}
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-4 py-1 rounded-bl-xl z-10 overflow-hidden rounded-tr-2xl">
                    MOST POPULAR
                  </div>
                )}

                <div className={`p-6 ${isPopular ? 'pt-10' : ''} overflow-visible`}>
                  {/* Plan Name */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-1 inline-flex items-center gap-1.5">
                    {plan.name}
                    <PlanInfoPopover plan={plan} isLastCard={isLastCard} />
                  </h3>
                  
                  {/* Tagline */}
                  <p className="text-sm text-gray-600 italic mb-4">
                    {plan.tagline}
                  </p>

                  {/* Pricing */}
                  {plan.isFree ? (
                    <div className="mb-6">
                      <span className="text-3xl font-bold text-gray-900">
                        FREE
                      </span>
                    </div>
                  ) : (
                    <div className="mb-6">
                      <div className="flex flex-col">
                        {billingCycle === 'monthly' ? (
                          <>
                            {plan.monthlyOriginalPrice && plan.monthlyOriginalPrice > (plan.monthlyPrice || 0) && (
                              <span className="text-sm text-gray-500 line-through mb-1">
                                ${plan.monthlyOriginalPrice.toFixed(2)}
                              </span>
                            )}
                            <span className="text-3xl font-bold text-gray-900">
                              ${(plan.monthlyPrice || 0).toFixed(2)}
                            </span>
                            <p className="text-sm text-gray-600 mt-1">
                              per month
                            </p>
                            {/* Founding Member Badge - Show for monthly */}
                            {!plan.isFree && (
                              <div className="inline-flex items-center bg-orange-500 text-white text-xs font-semibold px-3 py-1.5 rounded mt-2 whitespace-nowrap self-start">
                                50% OFF 🎉
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {billingCycle === 'annual' && plan.price && plan.price !== plan.annualPrice && plan.price > 0 && (
                              <span className="text-sm text-gray-500 line-through mb-1">
                                ${plan.price.toFixed(2)}
                              </span>
                            )}
                            {billingCycle === 'monthly' && plan.monthlyOriginalPrice && plan.monthlyOriginalPrice > (plan.monthlyPrice || 0) && (
                              <span className="text-sm text-gray-500 line-through mb-1">
                                ${plan.monthlyOriginalPrice.toFixed(2)}
                              </span>
                            )}
                            <span className="text-3xl font-bold text-gray-900">
                              ${billingCycle === 'annual' ? plan.annualPrice.toFixed(2) : (plan.monthlyPrice || 0).toFixed(2)}
                            </span>
                            <p className="text-sm text-gray-600 mt-1">
                              per {billingCycle === 'annual' ? 'year' : 'month'}
                            </p>
                            {/* Founding Member Badge - Show for both monthly and annual */}
                            {!plan.isFree && (
                              <div className="inline-flex items-center bg-orange-500 text-white text-xs font-semibold px-3 py-1.5 rounded mt-2 whitespace-nowrap self-start">
                                50% OFF 🎉
                              </div>
                            )}
                            {billingCycle === 'annual' && !plan.isFree && (
                              <p className="text-xs text-green-600 font-medium mt-2">
                                Get 2 months FREE with annual billing
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* CTA Button */}
                  {plan.isFree ? (
                    <Link
                      to="/register?type=business"
                      className="w-full btn btn-secondary mb-6 text-center"
                    >
                      Get Started
                    </Link>
                  ) : (
                    <button
                      className="w-full btn btn-secondary mb-6"
                      onClick={() => handleSelectPlan(plan.id)}
                    >
                      {(plan.id === 'trailhead' || plan.id === 'campfire') && isPlanConfigured(plan.id, billingCycle) ? (
                        <>
                          <LockClosedIcon className="h-4 w-4 mr-2" />
                          Checkout
                        </>
                      ) : 'Coming Soon'}
                    </button>
                  )}

                  {/* Features List */}
                  <ul className="space-y-3">
                    {/* Show "Everything in..." for higher tier plans */}
                    {plan.includesPreviousPlan && (
                      <li className="text-sm font-medium text-gray-600 italic mb-2">
                        Everything in {plan.includesPreviousPlan}, plus:
                      </li>
                    )}
                    
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-base text-gray-700">
                        <CheckIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          });
          })()}
            </div>

            {/* FAQ or Additional Info */}
            <div className="mt-16 max-w-3xl mx-auto text-center space-y-4">
              <p className="text-sm text-gray-500">
                * Coming Soon - These features are included in your plan and will be available shortly
              </p>
              <p className="text-gray-600">
                All plans include a 30-day money-back guarantee. No hidden fees.
              </p>
              <p className="text-sm text-gray-500">
                Questions? <a href="mailto:hello@campwhiz.com" className="text-primary hover:text-primary/80">Contact us</a> and we'll help you choose the right plan.
              </p>
            </div>
          </>
        ) : (
          /* Parents Pricing - Coming Soon */
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-200">
              <UserGroupIcon className="h-24 w-24 text-primary mx-auto mb-6" aria-hidden="true" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Parent Plans Coming Soon!
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                We're working on special pricing plans for parents to help you organize and plan your summer camp adventures.
              </p>
              <p className="text-gray-500 mb-8">
                Get early access by signing up for our newsletter.
              </p>
              <button className="btn btn-primary">
                Notify Me When Available
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Camp Selection Modal */}
      {showCampSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Select Your Camp
              </h2>
              <p className="text-gray-600 mb-6">
                Choose the camp you want to claim and purchase a plan for.
              </p>

              {/* Camp Search */}
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for your camp..."
                  value={campSearchTerm}
                  onChange={handleCampSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-gray-900"
                />
                
                {/* Search Results */}
                {showCampSearchResults && campSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-2 max-h-60 overflow-y-auto">
                    {campSearchResults.map((camp) => (
                      <div
                        key={camp.id}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSelectCamp(camp)}
                      >
                        <p className="font-medium text-gray-900">{camp.name}</p>
                        <p className="text-sm text-gray-500">{camp.city}, {camp.state}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Camp Display */}
              {selectedCampSlug && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-green-800 font-medium">
                    Selected: {campSearchTerm}
                  </p>
                </div>
              )}

              {/* Add New Camp Option */}
              <div className="border-t border-gray-200 pt-4 mb-6">
                <p className="text-sm text-gray-600 mb-3">
                  Can't find your camp?
                </p>
                <button
                  onClick={() => {
                    // For now, just show an alert. In the future, this could open a form or redirect to contact
                    alert('Please contact us at hello@campwhiz.com to add your camp to our directory.');
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add New Camp
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCampSelectionModal(false);
                    setSelectedCampSlug('');
                    setCampSearchTerm('');
                    setCampSearchResults([]);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmCampSelection}
                  disabled={!selectedCampSlug}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue to Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <AlreadyClaimedModal 
        isOpen={showAlreadyClaimedModal}
        onClose={() => setShowAlreadyClaimedModal(false)}
      />
    </div>
  );
}

